import type { App } from 'vue'
import * as Sentry from '@sentry/vue'
import { applyPlayerSentryIdentity } from '@/helpers/applyPlayerSentryIdentity'
import { isBenignVideoPlayInterruptError } from '@/helpers/reportPlayerVideoPlaybackToSentry'
import { isExpectedRegistrationClearSentryEvent } from '@/helpers/reportRegistrationClearedToSentry'
import DBService from './DBService'

export interface ISentryLucimarkPlayerContext {
  playerId: string
  registration: unknown
  storage: unknown
}

export interface ISentryService {}

/** TF.js COCO-SSD loads many multi-MB weight shards from GCS; tracing them only creates Sentry "large HTTP payload" noise. */
function shouldCreateSentrySpanForRequestUrl(url: string): boolean {
  if (url.includes('storage.googleapis.com/tfjs-models/')) {
    return false
  }
  return true
}

export class SentryService implements ISentryService {
  init(app: App) {
    const dsn = (import.meta.env.VITE_SENTRY_DSN ?? '').trim()
    const releaseFromEnv = (import.meta.env.VITE_COMMIT_HASH ?? '').trim()
    const release = releaseFromEnv || (typeof __COMMIT_HASH__ !== 'undefined' ? String(__COMMIT_HASH__).trim() : '')
    Sentry.init({
      app,
      dsn: dsn || undefined,
      release: release || undefined,
      environment: import.meta.env.MODE ?? 'development',
      enabled: dsn.length > 0,
      integrations: [
        Sentry.browserTracingIntegration({
          shouldCreateSpanForRequest: shouldCreateSentrySpanForRequestUrl
        }),
        Sentry.consoleLoggingIntegration()
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0, //  Capture 100% of the transactions
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: [
        /^https:\/\/api\.lucimark\.com\/api/,
        /^https:\/\/api\.staging\.lucimark\.com\/api/
      ],
      normalizeDepth: 10,
      enableLogs: true,
      enableMetrics: true,
      beforeSend(event, hint) {
        // Drop errors that originate from widget iframes (/w/<hash>/...).
        // Widgets run as same-origin iframes so their unhandled errors propagate
        // to the parent window; filter them out here to keep Sentry noise-free.
        const frames = event.exception?.values?.flatMap(v => v.stacktrace?.frames ?? []) ?? []
        const isFromWidget = frames.some(f => f.filename && /\/w\/[0-9a-f]+\//.test(f.filename))
        if (isFromWidget) {
          return null
        }
        const original = hint.originalException
        if (isBenignVideoPlayInterruptError(original)) {
          return null
        }
        const message = event.exception?.values?.[0]?.value ?? event.message ?? ''
        if (typeof message === 'string' && isBenignVideoPlayInterruptError(new Error(message))) {
          return null
        }
        if (isExpectedRegistrationClearSentryEvent(event)) {
          return null
        }
        return event
      }
    })
  }

  /**
   * Sets searchable tags + Context. Prefer calling after `init` and whenever registration or capacity changes.
   */
  async updateSentryContext(params: {
    playerId: string
    registrationStatus: unknown
    platform?: string | null
    androidIdPrefix?: string | null
    tenantHandle?: string | null
    capacityTier?: 'low' | 'mid' | 'high' | null
    lowMemoryMode?: boolean
  }) {
    const {
      playerId,
      registrationStatus,
      platform,
      androidIdPrefix,
      tenantHandle,
      capacityTier,
      lowMemoryMode
    } = params
    applyPlayerSentryIdentity({
      playerId,
      platform,
      androidIdPrefix,
      tenantHandle,
      capacityTier,
      lowMemoryMode
    })
    const playerContext: ISentryLucimarkPlayerContext = {
      playerId,
      registration: registrationStatus == null
        ? null
        : JSON.parse(JSON.stringify(registrationStatus)),
      storage: await DBService.getStorageEstimate()
    }
    Sentry.setContext('Lucimark Player', playerContext as unknown as { [key: string]: unknown })
  }
}

export type TSentryService = SentryService
